import type { AnchorHTMLAttributes, Context, ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cx } from './cx'
import { focusControl, interactionTransition } from './focus'

export interface NavigationCapability<Target> {
  readonly href: (target: Target) => string
  readonly navigate: (target: Target) => void
}

export const textLinkClass = cx(
  'rounded-sm text-primary underline underline-offset-2 hover:bg-muted',
  interactionTransition,
  focusControl,
)

export const navigationLinkClass = cx('-mx-1 px-1', textLinkClass)

export interface NavigationLinkProps<Target> extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick'
> {
  readonly to: Target
  readonly children: ReactNode
}

export interface NavigationProviderProps<Target> {
  readonly value: NavigationCapability<Target>
  readonly children: ReactNode
}

export interface NavigationCapabilityKit<Target> {
  readonly Context: Context<NavigationCapability<Target> | null>
  readonly Provider: (props: NavigationProviderProps<Target>) => ReactNode
  readonly useNavigation: () => NavigationCapability<Target>
  readonly Link: (props: NavigationLinkProps<Target>) => ReactNode
}

export function createNavigationCapability<Target>(
  screenName: string,
): NavigationCapabilityKit<Target> {
  const Context = createContext<NavigationCapability<Target> | null>(null)

  function Provider({ value, children }: NavigationProviderProps<Target>) {
    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  function useNavigation(): NavigationCapability<Target> {
    const navigation = useContext(Context)
    if (navigation === null) {
      throw new Error(`${screenName} screens must be rendered inside its NavigationProvider.`)
    }
    return navigation
  }

  function Link({ to, className, children, ...props }: NavigationLinkProps<Target>) {
    const navigation = useNavigation()
    return (
      <a
        href={navigation.href(to)}
        className={cx(navigationLinkClass, className)}
        onClick={(event) => {
          if (event.defaultPrevented) return
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          if (event.button !== 0) return
          event.preventDefault()
          navigation.navigate(to)
        }}
        {...props}
      >
        {children}
      </a>
    )
  }

  return { Context, Provider, useNavigation, Link }
}

export function useRouterAdapter<Target>(
  pathFor: (target: Target) => string,
): NavigationCapability<Target> {
  const navigate = useNavigate()

  return useMemo<NavigationCapability<Target>>(
    () => ({
      href: pathFor,
      navigate: (target: Target) => {
        void navigate({ to: pathFor(target) })
      },
    }),
    [navigate, pathFor],
  )
}
